import QUERY_KEYS from '@app/common/queryKeys';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

/**
 * Keeps the held-change counter honest after a visit is written anywhere in the app.
 *
 * Wired once into the global `MutationCache` in `App.tsx`, which covers every write that goes
 * through `useMutation` — the two scheduling hooks and anything added later. That is most of
 * them, and it is why this is not patched into call sites one at a time.
 *
 * It is NOT every write. `MutationCache` sees `useMutation` and nothing else, and two paths
 * call `updateVisitWithConflictHandling` directly with no mutation around it: the forced drag
 * reschedule in `SchedulingContainer` and the whole save path in `VisitEditForm`. Those call
 * `useRefreshPlanningSession` by hand. Any new direct caller must do the same — believing this
 * hook covers everything is how the next one gets written without it.
 *
 * The cache read comes first and nothing is invalidated unless a session is actually open, so
 * for the overwhelming majority of the app's writes this costs one map lookup.
 */
export const refreshPlanningSessionAfterMutation = (queryClient: QueryClient): void => {
  if (suspendCount > 0) return;

  const session = queryClient.getQueryData([QUERY_KEYS.PLANNING_SESSION_CURRENT]);
  if (!session) return;

  void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PLANNING_SESSION_CURRENT] });
};

/**
 * How many revert runs are in flight. Module state rather than a ref because the reader is the
 * `MutationCache` in `App.tsx`, which is constructed once outside React and cannot see one —
 * the same shape as `suppressNextGlobalErrorNotification` in the API interceptor.
 */
let suspendCount = 0;

/**
 * Pause the per-mutation refresh for a batch of writes, and hand back the release.
 *
 * `Revert all` is N sequential PUTs. Refreshing after each one refetches
 * `/planning-sessions/current`, which is not a cheap read any more — it recomputes the change
 * diff and the notify diff — so a forty-row revert would fire the endpoint forty times to show
 * a number that is wrong until the last one lands. The run invalidates once when it finishes.
 *
 * A counter, not a boolean, so overlapping batches cannot release each other's suspension.
 */
export const suspendPlanningSessionRefresh = (): (() => void) => {
  suspendCount += 1;
  let released = false;

  return () => {
    if (released) return;
    released = true;
    suspendCount -= 1;
  };
};

/**
 * The same refresh, for a write that is not a `useMutation` and so is invisible to the global
 * hook. Call it after the write resolves.
 */
export const useRefreshPlanningSession = (): (() => void) => {
  const queryClient = useQueryClient();
  return useCallback(() => refreshPlanningSessionAfterMutation(queryClient), [queryClient]);
};

/** Whether a batch is holding the refresh — for callers that do their own invalidating. */
export const isPlanningSessionRefreshSuspended = (): boolean => suspendCount > 0;

export default refreshPlanningSessionAfterMutation;
